import { Component, ChangeDetectionStrategy, ViewEncapsulation, input, computed } from '@angular/core';

export type FvLoaderStyle = 'line-simple' | 'line-spinner' | 'dot-circle';
export type FvLoaderSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/** Dimension in px for each size */
const SIZE_MAP: Record<FvLoaderSize, number> = {
  xxs: 16,
  xs: 24,
  sm: 32,
  md: 48,
  lg: 56,
  xl: 64,
};

/** Stroke width for line styles */
const STROKE_MAP: Record<FvLoaderSize, number> = {
  xxs: 2,
  xs: 2,
  sm: 3,
  md: 4,
  lg: 4,
  xl: 5,
};

@Component({
  selector: 'fv-loader',
  standalone: true,
  templateUrl: './loader.component.html',
  styleUrl: './loader.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': 'hostClasses()' },
})
export class FvLoaderComponent {
  /** Loader visual variant */
  readonly variant = input<FvLoaderStyle>('line-simple');

  /** Loader size */
  readonly size = input<FvLoaderSize>('sm');

  /** Whether the loader is active (shows spinner, blocks content) */
  readonly loading = input(true);

  /** Whether to show a dimming overlay over projected content when loading */
  readonly overlay = input(true);

  /** Supporting text below the spinner (empty = hidden) */
  readonly text = input('');

  /**
   * Stretch the projected content to the loader's own height instead of letting it size itself.
   * Needed when the layout sizes the loader (flex/grid item) and the projected content is a scroll
   * container: without it the scroller has no definite parent height and grows to its content.
   * Only meaningful in overlay mode (content projected).
   */
  readonly fill = input(false);

  readonly hostClasses = computed(() => {
    const classes = ['fv-loader', `fv-loader--${this.variant()}`, `fv-loader--${this.size()}`];
    if (this.loading()) {
      classes.push('fv-loader--loading');
    }
    if (this.fill()) {
      classes.push('fv-loader--fill');
    }
    return classes.join(' ');
  });

  /** Dimension in px */
  readonly dimension = computed(() => SIZE_MAP[this.size()]);

  /** Stroke width for SVG line styles */
  readonly strokeWidth = computed(() => STROKE_MAP[this.size()]);

  /** SVG circle radius (dimension/2 - strokeWidth/2) */
  readonly radius = computed(() => {
    const d = this.dimension();
    const sw = this.strokeWidth();
    return (d - sw) / 2;
  });

  /** SVG circle circumference */
  readonly circumference = computed(() => 2 * Math.PI * this.radius());

  /** Stroke dasharray: ~75% arc for line-simple, ~60% for line-spinner */
  readonly dashArray = computed(() => {
    const c = this.circumference();
    if (this.variant() === 'line-spinner') {
      return `${c * 0.6} ${c * 0.4}`;
    }
    return `${c * 0.75} ${c * 0.25}`;
  });

  /** Center coordinate for SVG */
  readonly center = computed(() => this.dimension() / 2);

  /** Dot radius for dot-circle style — small enough to show clear gaps */
  readonly dotRadius = computed(() => {
    const d = this.dimension();
    if (d <= 24) return 1;
    if (d <= 32) return 1.5;
    if (d <= 48) return 2;
    if (d <= 56) return 2.5;
    return 3;
  });

  /** Ring radius for dot placement — dots sit near the edge */
  readonly dotRingRadius = computed(() => {
    const d = this.dimension();
    return d / 2 - this.dotRadius() - 0.5;
  });

  /** Array of 12 dots with cx, cy, opacity — angular gradient from solid to faded */
  readonly dots = computed(() => {
    const cx = this.center();
    const cy = this.center();
    const r = this.dotRingRadius();
    const count = 12;
    return Array.from({ length: count }, (_, i) => {
      const angle = (i * 30 - 90) * (Math.PI / 180);
      // Opacity from 1.0 (top/12 o'clock) down to ~0.15 going clockwise
      const opacity = 1 - i * (0.85 / (count - 1));
      return {
        x: cx + r * Math.cos(angle),
        y: cy + r * Math.sin(angle),
        opacity: Math.round(opacity * 100) / 100,
      };
    });
  });
}
